import { Field, FieldProps, ErrorMessage } from "formik"
import { classNames } from "primereact/utils"
import { forwardRef, LegacyRef, PropsWithChildren } from "react"
import { InputText } from "primereact/inputtext"
import { InputMask } from "primereact/inputmask"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faXmark } from "@fortawesome/pro-light-svg-icons"
import { AnyObject } from "yup/lib/types"

const InputField = forwardRef<HTMLInputElement, PropsWithChildren<Props>>(function InputField(
  {
    field,
    label,
    type,
    mask,
    unmask,
    horizontal,
    horizontalReverse,
    floatLabel,
    className,
    inputClassName,
    placeholder,
    validation,
    disabled,
    autocomplete,
    children,
    truncate = true,
    clearable,
    onClear,
    blockSpecialChars,
    spellcheck,
    onChange: onValueChange,
  },
  inputRef,
) {
  return (
    <Field name={field} validate={validation}>
      {({ field: { name, value, onChange }, meta: { touched, error }, form: { setFieldValue } }: FieldProps) => (
        <div
          className={classNames(
            "field space-y-2 relative",
            horizontal && "inline-flex justify-between",
            horizontalReverse && "inline-flex flex-row-reverse justify-end gap-2 items-center",
            floatLabel && "float-label relative block mt-1",
            !horizontal && !horizontalReverse && !floatLabel && "flex flex-col",
            className,
          )}
        >
          {children}
          {label && !floatLabel && (
            <label
              htmlFor={name}
              title={label}
              className={classNames("font-medium text-gray-500 text-sm" + (truncate ? " truncate" : ""), {
                "mr-3 mb-0 mt-2": horizontal,
              })}
            >
              {label}
            </label>
          )}
          <div
            className={classNames("flex flex-col", {
              "w-full": !horizontalReverse,
              "relative w-1/3": horizontalReverse,
            })}
          >
            {clearable && (
              <div
                className="absolute flex justify-center items-center right-2 bottom-6 z-20 w-4 h-4 rounded-full hover:bg-gray-200 hover:ring-1 ring-primary cursor-pointer"
                title="Remove item"
                onClick={() => {
                  onClear?.() ?? setFieldValue(name, undefined)
                }}
              >
                <FontAwesomeIcon icon={faXmark} className="text-xs" />
              </div>
            )}
            {mask ? (
              <InputMask
                type={type}
                mask={mask}
                unmask={unmask}
                id={name}
                name={name}
                onChange={(e) => {
                  onChange(e)
                  onValueChange?.(e.value)
                }}
                value={value}
                disabled={disabled}
                className={classNames(
                  "p-inputtext-sm h-10",
                  { "p-invalid": touched && error, horizontal: horizontal },
                  { "pr-5": clearable },
                  inputClassName,
                )}
                ref={inputRef as LegacyRef<InputMask>}
              />
            ) : (
              <InputText
                autoComplete={autocomplete}
                spellCheck={spellcheck}
                aria-autocomplete="none"
                type={type ?? "text"}
                id={name}
                name={name}
                placeholder={placeholder}
                onChange={(e) => {
                  if (blockSpecialChars)
                    e.currentTarget.value = e.currentTarget.value
                      .replaceAll(specialCharRegex, "")
                      .replace("  ", "")
                      .trimStart()
                  onChange?.(e)
                  onValueChange?.(e.target.value)
                }}
                value={value}
                disabled={disabled}
                className={classNames(
                  "p-inputtext-sm h-10",
                  { "p-invalid": touched && error, horizontal: horizontal },
                  { "pr-5": clearable },
                  inputClassName,
                )}
                ref={inputRef}
              />
            )}

            {label && floatLabel && (
              <label htmlFor={name} className="text-sm text-gray-400 left-3 top-2 absolute transition-all ease-in-out">
                {label}
              </label>
            )}
            <div className="flex items-start p-error h-2 mt-1">
              {touched && error && <ErrorMessage name={field}>{(msg) => <small>{msg}</small>}</ErrorMessage>}
            </div>
          </div>
        </div>
      )}
    </Field>
  )
})

const specialCharRegex = /[^ \w]+\W*/g

type Props = {
  field: string
  label?: string
  type?: string
  mask?: string
  unmask?: boolean
  className?: string
  horizontal?: boolean
  horizontalReverse?: boolean
  floatLabel?: boolean
  inputClassName?: string
  placeholder?: string
  validation?(value: string): void
  disabled?: boolean
  autocomplete?: string
  spellcheck?: boolean
  fieldName?: string
  truncate?: boolean
  clearable?: boolean
  onClear?(): void
  blockSpecialChars?: boolean
  onChange?(value: AnyObject | number | string | null | undefined): void
}

export { InputField }
